import { apiRequest } from "./api";

// Maps the wizard's biz object (src/data/rates.js: blankBiz) onto the
// backend's BusinessDetailsRequest shape.
function toRequestBody(biz) {
  const addressKind = biz.kind === "foreign" ? "foreign" : "US";
  const body = {
    ein: biz.ein,
    businessNameLine1: biz.name,
    businessNameLine2: biz.dba || "",
    taxPreparerId: biz.taxPreparerId || null,
    businessNameControl: biz.ctl,
    inCareOfName: biz.care || "",
    businessTypeCode: biz.businessType,
    email: biz.email,
    addressKind,
    phone: biz.phone ? biz.phone.replace(/\D/g, "") : "",
    signingAuthorityName: biz.signingAuthorityName,
    signingAuthorityTitle: biz.signingAuthorityTitle,
    signingAuthorityPhone: biz.signingAuthorityPhone ? biz.signingAuthorityPhone.replace(/\D/g, "") : "",
    thirdPartyDesignee: biz.hasThirdPartyDesignee,
    thirdPartyDesigneeName: biz.hasThirdPartyDesignee ? biz.tpdName : "",
    thirdPartyDesigneePhone: biz.hasThirdPartyDesignee ? biz.tpdPhone.replace(/\D/g, "") : "",
    thirdPartyDesigneePin: biz.hasThirdPartyDesignee ? biz.tpdPin : "",
  };
  if (addressKind === "US") {
    body.usAddress = {
      addressLine1: biz.a1,
      addressLine2: biz.a2 || "",
      city: biz.city,
      state: biz.state,
      zip: biz.zip.replace(/\D/g, ""),
    };
  } else {
    body.foreignAddress = {
      addressLine1: biz.a1,
      addressLine2: biz.a2 || "",
      city: biz.city,
      province: biz.state,
      country: biz.country || biz.countryName || "",
      postalCode: biz.zip || "",
    };
  }
  return body;
}

// Maps a BusinessDetailsResponse back onto the wizard's biz object shape.
function fromResponseBody(response) {
  const isUs = response.addressKind === "US";
  const address = (isUs ? response.usAddress : response.foreignAddress) || {};
  return {
    id: response.id,
    name: response.businessNameLine1 || "",
    dba: response.businessNameLine2 || "",
    taxPreparerId: response.taxPreparerId || null,
    ein: response.ein || "",
    ctl: response.businessNameControl || "",
    care: response.inCareOfName || "",
    businessType: response.businessTypeCode || "",
    email: response.email || "",
    kind: isUs ? "us" : "foreign",
    // address.country is the isoCode (e.g. "CA"), not a display name - the
    // backend stores/returns it as-is (Return2290Assembler sends it straight
    // through as ForeignAddressType/CountryCd). Map it directly into
    // `country` so the Country <Select> can preselect it on edit; countryName
    // is just a display label, backfilled from the loaded country list below.
    country: isUs ? "US" : (address.country || ""),
    countryName: isUs ? "United States" : "",
    a1: address.addressLine1 || "",
    a2: address.addressLine2 || "",
    city: address.city || "",
    state: (isUs ? address.state : address.province) || "",
    zip: (isUs ? address.zip : address.postalCode) || "",
    phone: response.phone || "",
    signingAuthorityName: response.signingAuthorityName || "",
    signingAuthorityTitle: response.signingAuthorityTitle || "",
    signingAuthorityPhone: response.signingAuthorityPhone || "",
    hasThirdPartyDesignee: !!response.thirdPartyDesignee,
    tpdName: response.thirdPartyDesigneeName || "",
    tpdPhone: response.thirdPartyDesigneePhone || "",
    tpdPin: response.thirdPartyDesigneePin || "",
  };
}

export async function createBusiness(biz) {
  const data = await apiRequest("/businesses", { method: "POST", body: toRequestBody(biz) });
  return fromResponseBody(data);
}

export async function updateBusiness(businessId, biz) {
  const data = await apiRequest(`/businesses/${businessId}`, { method: "PATCH", body: toRequestBody(biz) });
  return fromResponseBody(data);
}

export async function getBusiness(businessId) {
  const data = await apiRequest(`/businesses/${businessId}`);
  return fromResponseBody(data);
}

// GET /businesses -> the caller's own businesses, newest first.
export async function listBusinesses() {
  const data = await apiRequest("/businesses");
  return Array.isArray(data) ? data.map(fromResponseBody) : [];
}

// Creates the business record, or updates it in place if we already have its
// id from an earlier save this session.
export async function saveBusiness(businessId, biz) {
  if (businessId) {
    return updateBusiness(businessId, biz);
  }
  return createBusiness(biz);
}

// DELETE /businesses/{id} -> soft-deletes the business (backend sets
// deleted_at; it stops appearing in listBusinesses(), but any return that
// already references it keeps showing its name/EIN in My Filings).
export async function deleteBusiness(businessId) {
  await apiRequest(`/businesses/${businessId}`, { method: "DELETE" });
}
